// Транспорт и хуки реестра РМРС (зеркало портала 55, таблицы p55_rs_*). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts + api/hooks.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/api/p55/rs/*) со своими
// фильтрами. Разбор ответа и классы ошибок переиспользуем — заводить свои значило бы получить
// вторую обработку 401 и вторую страницу логина.
//
// Реестр РМРС — ПУБЛИЧНЫЙ СПРАВОЧНИК Регистра, а не наши данные. Писать в него нельзя ни из
// CRM, ни откуда-либо: источник — обход rs-class.org скрапером, и любая правка была бы затёрта
// (см. шапку p55_api.py и schema_55.sql).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import { строкаЗапроса } from '../lib/p55'
import type { ФильтрыP55 } from '../lib/p55'

const БАЗА = '/api/p55/rs'

/** Тот же разбор ответа, что в api/client.ts: 401 → на логин, остальное — ApiError. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица читалась бы как
    // «в реестре такого нет», поэтому поднимаем тот же тип, что и остальные разделы:
    // QueryState печатает по нему честное объяснение вместо пустого списка.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p55_api.py) ───────────

/** Пометка BFF «выборка сужена по вашей зоне». У реестра сужать нечего, но если она вдруг
 *  придёт — экран обязан её показать, а не рисовать неполноту как полноту. */
export interface ПометкаОхвата {
  limited?: boolean
  partial?: boolean
  note?: string
}

export interface СтраницаP55<T> extends ПометкаОхватаВОтвете {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  /** «нет» | «с начала строки» | «внутри строки» — как именно искали. */
  search_mode?: string
}

interface ПометкаОхватаВОтвете {
  _scope?: ПометкаОхвата
}

/** Наше судно, найденное по IMO. count — сколько всего наших записей с этим IMO. */
export interface НашеСудно {
  bitrix_id: number | null
  name: string | null
  flag: string | null
  home_port: string | null
  count: number | null
}

export interface СудноРС {
  imo: string
  rs_number: string | null
  name: string | null
  flag: string | null
  port_of_registry: string | null
  call_sign: string | null
  /** Ниже — колонки, которые скрапер завёл, но НЕ наполняет:
   *  во всех 4 186 строках пусто (на 28.07.2026). */
  class_notation: string | null
  vessel_type: string | null
  owner: string | null
  build_year: number | null
  gross_tonnage: number | null
  last_survey: string | null
  next_survey: string | null
  survey_postpone: string | null
  in_rs: boolean | null
  source_url: string | null
  first_seen: string | null
  scraped_at: string | null
  updated_at: string | null
  /** Мостик к нашему флоту: одна запись (их бывает несколько — см. count). */
  ours: НашеСудно | null
}

export interface СервисникВПорту {
  company: string
  city: string | null
  country: string | null
  valid_to_last: string | null
  lines: number
  scopes: number
  authorizers: number
  any_valid: boolean | null
  doc_url: string | null
  is_ours: boolean
}

export interface КарточкаСудна extends СудноРС {
  content_hash: string | null
  /** Все наши записи с этим IMO, а не одна: дубли контактов надо видеть. */
  our_vessels: Array<{
    bitrix_id: number
    name: string | null
    flag: string | null
    home_port: string | null
  }>
  /** Что делали МЫ на этом судне: единственный ответ на «что там стоит» (в реестре его нет). */
  our_work: { equip_acts: number; verifications: number }
  /** Город сервисника, сопоставленный с портом приписки. null — сопоставления нет. */
  port_city: string | null
  suppliers_in_port: СервисникВПорту[]
  data_notes: Record<string, string>
}

export interface ИзделиеРС {
  src_id: string
  nomen_code: string | null
  /** Название группы номенклатуры. Пусто во всех строках — есть только код. */
  nomen_group: string | null
  /** Пусто в 4 101 строке (на 28.07.2026); непустое ВСЕГДА начинается
   *  с номера пункта («1. Эжекторы…»). */
  product_name: string | null
  manufacturer: string | null
  manufacturer_country: string | null
  certificate_no: string | null
  certificate_type: string | null
  valid_from: string | null
  valid_to: string | null
  status: string | null
  doc_url: string | null
  first_seen: string | null
  scraped_at: string | null
  updated_at: string | null
  expired: boolean | null
  /** Дней до конца срока; null — срока в данных нет. */
  days_left: number | null
}

export interface КарточкаИзделия extends ИзделиеРС {
  content_hash: string | null
  same_manufacturer: ИзделиеРС[]
  as_supplier: Array<{
    company: string
    city: string | null
    country: string | null
    certificate_no: string | null
    valid_to: string | null
    is_competitor: boolean | null
  }>
  our_company: Array<{ bitrix_id: number; title: string | null; company_type: string | null }>
  data_notes: Record<string, string>
}

/** Строка списка сервисников — это КОМПАНИЯ (в данных на компанию до 50 строк). */
export interface СервисникГруппа {
  company: string
  is_ours: boolean
  country: string | null
  city: string | null
  is_competitor: boolean | null
  lines: number
  certificates: number
  scopes: number
  authorizers: number
  valid_to_first: string | null
  valid_to_last: string | null
  lines_valid: number
  lines_expired: number
  doc_url: string | null
}

export interface СтрокаПризнания {
  src_id: string
  company: string
  country: string | null
  city: string | null
  certificate_no: string | null
  service_scope: string | null
  activity: string | null
  /** По чьему поручению признан: изготовитель оборудования (FURUNO, VIKING). */
  authorizer: string | null
  /** Пусто во всех 2 652 строках (на 28.07.2026). */
  valid_from: string | null
  valid_to: string | null
  status: string | null
  is_competitor: boolean | null
  doc_url: string | null
  expired: boolean | null
  days_left: number | null
  first_seen: string | null
  scraped_at: string | null
  updated_at: string | null
}

export interface ОбластьПризнания {
  scope: string
  lines: number
  authorizers: string[]
  valid_to_last: string | null
  expired_lines: number
}

export interface КарточкаСервисника {
  company: string
  country: string | null
  city: string | null
  is_competitor: boolean | null
  is_ours: boolean
  certificates: string[]
  lines: number
  valid_to_first: string | null
  valid_to_last: string | null
  scopes: ОбластьПризнания[]
  rows: СтрокаПризнания[]
  products: ИзделиеРС[]
  our_company: Array<{
    bitrix_id: number
    title: string | null
    company_type: string | null
    industry: string | null
  }>
  data_notes: Record<string, string>
}

export interface ПараКоличество {
  k: string
  n: number
}

export interface СправочникиP55 extends ПометкаОхватаВОтвете {
  vessel_flag: ПараКоличество[]
  vessel_port: ПараКоличество[]
  product_cert_type: ПараКоличество[]
  product_country: ПараКоличество[]
  product_nomen: ПараКоличество[]
  supplier_country: ПараКоличество[]
  supplier_city: ПараКоличество[]
  supplier_scope: Array<{ scope: string; lines: number; companies: number }>
  counts: {
    vessels: number
    products: number
    supplier_lines: number
    suppliers: number
    our_vessels: number
    our_vessels_with_imo: number
    /** Наших ЗАПИСЕЙ, совпавших по IMO (с дублями). */
    our_vessels_in_rs: number
    /** Судов РЕЕСТРА, у которых есть наша запись. Меньше предыдущего — из-за дублей. */
    rs_vessels_ours: number
    our_imo_duplicated: number
  }
  /** Сколько строк реально имеет значение в колонке. Ноль → колонки в данных нет. */
  filled_vessels: Record<string, number>
  filled_products: Record<string, number>
  filled_suppliers: Record<string, number>
  validity: {
    products_no_date: number
    products_expired: number
    products_expiring: number
    products_active: number
    supplier_lines_expired: number
    supplier_lines_expiring: number
    supplier_lines_active: number
  }
  synced: {
    vessels: string | null
    products: string | null
    suppliers: string | null
    /** Когда скрапер последний раз обходил rs-class.org. Это НЕ время синка. */
    scraped_vessels: string | null
    scraped_products: string | null
    scraped_suppliers: string | null
  }
  /** Колонки, пустые во ВСЕХ строках. По ним экран печатает «в данных нет». */
  empty_columns: { vessels: string[]; products: string[]; suppliers: string[] }
  expiring_days: number
  /** Порт приписки (латиницей) → город сервисника (по-русски). */
  port_city: Record<string, string>
  /** Наши юрлица, как их пишет Регистр. */
  ours_in_registry: string[]
  data_notes: Record<string, string>
}

// ─────────── хуки ───────────
// staleTime большой: зеркало обновляется синком раз в час, а сам реестр — скрапером и вовсе
// раз в несколько недель. Чаще спрашивать нечего.
// placeholderData — чтобы таблица не мигала пустотой при смене фильтра.

const общее = { retry: 1, staleTime: 5 * 60_000 } as const

export const useP55Refs = () =>
  useQuery({
    queryKey: ['p55', 'rs', 'refs'],
    queryFn: () => запрос<СправочникиP55>('/refs'),
    staleTime: 30 * 60_000,
    retry: 1,
  })

export const useСудаРС = (ф: ФильтрыP55) =>
  useQuery({
    queryKey: ['p55', 'rs', 'vessels', ф],
    queryFn: () => запрос<СтраницаP55<СудноРС>>('/vessels' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useСудноРС = (imo: string | undefined) =>
  useQuery({
    queryKey: ['p55', 'rs', 'vessel', imo],
    queryFn: () => запрос<{ row: КарточкаСудна }>(`/vessel?imo=${encodeURIComponent(imo!)}`)
      .then((r) => r.row),
    enabled: !!imo,
    ...общее,
  })

export const useИзделияРС = (ф: ФильтрыP55) =>
  useQuery({
    queryKey: ['p55', 'rs', 'products', ф],
    queryFn: () => запрос<СтраницаP55<ИзделиеРС>>('/products' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useИзделиеРС = (id: string | undefined) =>
  useQuery({
    queryKey: ['p55', 'rs', 'product', id],
    queryFn: () => запрос<{ row: КарточкаИзделия }>(`/product?id=${encodeURIComponent(id!)}`)
      .then((r) => r.row),
    enabled: !!id,
    ...общее,
  })

export const useСервисникиРС = (ф: ФильтрыP55) =>
  useQuery({
    queryKey: ['p55', 'rs', 'suppliers', ф],
    queryFn: () => запрос<СтраницаP55<СервисникГруппа>>('/suppliers' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useСервисникРС = (company: string | undefined) =>
  useQuery({
    queryKey: ['p55', 'rs', 'supplier', company],
    queryFn: () => запрос<{ row: КарточкаСервисника }>(
      `/supplier?company=${encodeURIComponent(company!)}`).then((r) => r.row),
    enabled: !!company,
    ...общее,
  })
